import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import { type FC, type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { DISABLED_CONTROL, FONT_SIZE, ICON_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, STEPPER } from "../../../theme/tokens";

export type SystemStepperSize = "standard" | "large";

type Props = {
	value: number;
	/** Called with the next value, already clamped to `[min, max]`. */
	onChange: (next: number) => void;
	min: number;
	max: number;
	/** The accessible name of the number ("How many shifts"). Already translated. */
	label: string;
	/** `large` makes the number the figure of its surface; `standard` sits in a form row. */
	size?: SystemStepperSize;
	disabled?: boolean;
	/** On the root; `-decrement`, `-increment` and `-value` go on the parts. */
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** Keys the value answers to, as the WAI-ARIA spinbutton pattern lists them. */
const KEY_DELTA: Record<string, number> = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 };

/**
 * "How many" as − and + around the number — for a small count a person nudges (people to post, nurses still
 * needed), where a dropdown of 1–100 hides the answer behind a tap and a free number field summons a keyboard.
 *
 * The number itself is the control to assistive tech (`role="spinbutton"` with its value and bounds, arrow keys,
 * Home / End), and the buttons are its touch affordance, out of the tab order as the pattern prescribes. Each button
 * greys out at its end of the range rather than disappearing, so the row never reflows.
 *
 * A new value ticks in from the direction it moved — up when it grows, down when it shrinks — so a tap reads as a
 * change even when the eye is on the button, not the number. Movement is dropped under reduced motion; the fade stays.
 */
// DEFAULT: `size` absent is the form-row stepper and `disabled` absent is enabled — the plain case, not missing data.
export const SystemStepper: FC<Props> = ({ value, onChange, min, max, label, size = "standard", disabled = false, dataTestId, sx }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	// The direction of the last change, read by the tick. Derived from the previous value during render — React's pattern
	// for state that follows a prop — rather than written to a ref mid-render, which React may run twice or throw away.
	const [tick, setTick] = useState<{ value: number; direction: 1 | -1 }>({ value, direction: 1 });
	if (tick.value !== value) setTick({ value, direction: value > tick.value ? 1 : -1 });

	const set = (next: number): void => {
		const clamped = Math.min(max, Math.max(min, next));
		if (clamped !== value) onChange(clamped);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		if (disabled) return;
		const delta = KEY_DELTA[event.key];
		if (delta !== undefined) set(value + delta);
		else if (event.key === "Home") set(min);
		else if (event.key === "End") set(max);
		else return;
		event.preventDefault();
	};

	const isLarge = size === "large";
	const buttonSx = {
		flex: "none",
		width: STEPPER.BUTTON_SIZE,
		height: STEPPER.BUTTON_SIZE,
		display: "grid",
		placeItems: "center",
		p: 0,
		border: 0,
		borderRadius: "50%",
		bgcolor: NEUTRAL[150],
		color: INK.PRIMARY,
		cursor: "pointer",
		WebkitTapHighlightColor: "transparent",
		transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
		"@media (hover: hover) and (pointer: fine)": { "&:hover:not(:disabled)": { bgcolor: NEUTRAL[200] } },
		"&:active:not(:disabled)": { transform: "scale(0.94)" },
		"&:disabled": { cursor: "default", color: INK.DISABLED, bgcolor: NEUTRAL[50] },
		"& svg": { fontSize: ICON_SIZE.MENU_ROW },
		"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
	} as const;

	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{ display: "inline-flex", alignItems: "center", gap: isLarge ? 0.5 : 0.25, opacity: disabled ? DISABLED_CONTROL.DIMMED_OPACITY : 1 },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				component="button"
				type="button"
				tabIndex={-1}
				aria-label={t("system.stepper.decrease", { label })}
				disabled={disabled || value <= min}
				onClick={() => set(value - 1)}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-decrement`}
				sx={buttonSx}
			>
				<RemoveIcon />
			</Box>
			<Box
				role="spinbutton"
				tabIndex={disabled ? -1 : 0}
				aria-label={label}
				aria-valuenow={value}
				aria-valuemin={min}
				aria-valuemax={max}
				aria-disabled={disabled || undefined}
				onKeyDown={handleKeyDown}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-value`}
				sx={{
					minWidth: STEPPER.VALUE_MIN_WIDTH[size],
					textAlign: "center",
					fontSize: isLarge ? FONT_SIZE.STAT_VALUE : FONT_SIZE.ROW_LABEL,
					fontWeight: 800,
					lineHeight: 1.1,
					letterSpacing: LETTER_SPACING.TIGHT_FIGURE,
					fontVariantNumeric: "tabular-nums",
					color: INK.PRIMARY,
					borderRadius: 1,
					outline: "none",
					"&:focus-visible": { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
				}}
			>
				<Box
					component="span"
					// Keyed by value so the tick replays on every change, and only then.
					key={value}
					sx={{
						display: "inline-block",
						// The render that adjusts `tick` is re-run before it commits, so the committed one always reads the new direction.
						"--stepper-rise": `${tick.direction * STEPPER.TICK_RISE_PX}px`,
						"@keyframes stepperTick": {
							from: { opacity: 0, transform: "translateY(var(--stepper-rise))" },
							to: { opacity: 1, transform: "translateY(0)" },
						},
						"@keyframes stepperFade": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: `stepperTick ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: `stepperFade ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}` },
					}}
				>
					{value}
				</Box>
			</Box>
			<Box
				component="button"
				type="button"
				tabIndex={-1}
				aria-label={t("system.stepper.increase", { label })}
				disabled={disabled || value >= max}
				onClick={() => set(value + 1)}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-increment`}
				sx={buttonSx}
			>
				<AddIcon />
			</Box>
		</Box>
	);
};
